import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/page-layout/PageHeader";

import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  Box,
  Stack,
  InputAdornment,
  CardContent,
  CardMedia,
  Card,
  IconButton,
  Chip,
  Paper,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import {
  AddPhotoAlternate,
  CurrencyRupee,
  Map,
  Payment,
  Timer,
} from "@mui/icons-material";
import FormikTextField from "../../../components/form/FormikTextField";
import moment from "moment";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../store/loaderSlice";
import { errorHandler } from "../../../utils/errorHandler";
import axiosInstance from "../../../utils/axiosInstance";
import { storesUrl } from "../../../utils/urls";
import { useNavigate, useParams } from "react-router";
import { toast } from "react-hot-toast";

const validationSchema = Yup.object({
  name: Yup.string().required("Store name is required"),
  openTime: Yup.string().required("Open time is required"),
  closeTime: Yup.string().required("Close time is required"),
  latitude: Yup.number().required("Latitude is required"),
  longitude: Yup.number().required("Longitude is required"),
  // image: Yup.mixed().required("Banner image is required"),
  platformFee: Yup.number()
    .min(0, "Platform fee must be 0 or greater")
    .required("Platform fee is required"),
});

const EditStore = ({ setFormData, onBack, nextStep }) => {
  const [imagePreview, setImagePreview] = React.useState(null);
  const navigate = useNavigate();
  const [initialValues, setInitalValues] = useState({
    name: "",
    openTime: "",
    closeTime: "",
    latitude: "",
    longitude: "",
    imageUrl: null,
    status: "ACTIVE",
    platformFee: 0,
  });

  const handleImageChange = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    setFieldValue("image", file);
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };
  const dispatch = useDispatch();
  const { id } = useParams();

  useEffect(() => {
    dispatch(showLoader("Fetching store data..."));
    axiosInstance
      .get(`${storesUrl}/${id}`)
      .then((res) => setInitalValues(res.data))
      .catch(errorHandler)
      .finally(() => {
        dispatch(hideLoader());
      });
  }, [id, dispatch]);

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          dispatch(showLoader("Updating store data..."));
            // convert values to formData
            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) => {
            // For imageUrl, check if it's a File, otherwise skip (keep existing image)
            if (key === "imageUrl") {
              if (value instanceof File) {
              formData.append("image", value);
              }
            } else {
              formData.append(key, value);
            }
            });
          axiosInstance
            .patch(`${storesUrl}/${id}`, formData)
            .then(() => {
              toast.success("Store updated successfully!");
              navigate(-1);
            })
            .catch(errorHandler)
            .finally(() => {
              dispatch(hideLoader());
              setSubmitting(false);
            });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          dirty,
          isValid,
          handleSubmit,
        }) => (
          <>
            <Box>
              <PageHeader
                title={"Store Details"}
                lead={
                  "Please fill in the store details and upload a banner image to continue."
                }
                showBack={true}
                buttonTitle={"Save Changes"}
                buttonOnClick={() => {
                  handleSubmit();
                }}
              />

              <Card sx={{ borderRadius: 4, display: { md: "flex" }, mb: 4 }}>
                <CardMedia
                  component="img"
                  sx={{ width: 280, aspectRatio: "16/9", bgcolor: grey[200] }}
                  image={
                    typeof values.imageUrl === "string"
                      ? values.imageUrl
                      : values.imageUrl
                        ? URL.createObjectURL(values.imageUrl)
                        : null
                  }
                  alt="Store Banner"
                />
                <CardContent sx={{ flex: 1 }}>
                  <Box display="flex" direction={"row"} gap={2}>
                    <Typography variant="h5">{values.name}</Typography>

                    <Chip
                      variant="filled"
                      color={values.status === "ACTIVE" ? "success" : "warning"}
                      label={
                        values.status === "ACTIVE" ? "Active" : "Not Active"
                      }
                    />
                  </Box>
                  <Box display={"flex"} alignItems={"center"} gap={2}>
                    <Box display="flex" py={2}>
                      <Paper
                        elevation={2}
                        sx={{
                          px: 2,
                          py: 1,
                          display: "flex",
                          gap: 1,
                          borderRadius: 4,
                        }}
                      >
                        <Timer color="primary" />
                        <Typography variant="body1" color="primary">
                          {moment(values.openTime, "HH:mm").format("hh:mm A")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          —
                        </Typography>

                        <Typography variant="body1" color="error">
                          {moment(values.closeTime, "HH:mm").format("hh:mm A")}
                        </Typography>
                      </Paper>
                    </Box>

                    <Button
                      variant="outlined"
                      startIcon={<Map />}
                      size="large"
                      onClick={() => {
                        if (values.latitude && values.longitude) {
                          const url = `https://www.google.com/maps?q=${values.latitude},${values.longitude}`;
                          window.open(url, "_blank");
                        }
                      }}
                    >
                      See Location
                    </Button>

                    <Paper
                      elevation={2}
                      sx={{
                        px: 2,
                        py: 1,
                        display: "flex",
                        gap: 1,
                        borderRadius: 4,
                      }}
                    >
                      <Payment color="success" />
                      <Typography>
                        Platform Fee: ₹{values.platformFee}
                      </Typography>
                    </Paper>
                  </Box>
                </CardContent>
              </Card>

              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={6}>
                    <Box
                      sx={{
                        borderWidth: 1,
                        borderStyle: values.image ? "solid" : "dashed",
                        borderColor: grey[300],
                        borderRadius: 2,
                        p: 1,
                        aspectRatio: "16/9",
                      }}
                    >
                      {values.imageUrl ? (
                        <Box
                          display="flex"
                          justifyContent="center"
                          alignItems={"flex-end"}
                          position={"relative"}
                          height={"100%"}
                        >
                          <img
                            src={
                              typeof values.imageUrl === "string"
                                ? values.imageUrl
                                : values.imageUrl
                                  ? URL.createObjectURL(values.imageUrl)
                                  : null
                            }
                            alt="Preview"
                            style={{
                              width: "100%",
                              borderRadius: 8,
                              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                              position: "absolute",
                              top: 0,
                              left: 0,
                            }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            component="label"
                          >
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              name="imageUrl"
                              id="image-upload"
                              onChange={(e) => {
                                setFieldValue("imageUrl", e.target.files[0]);
                              }}
                            />
                            Re-Upload Banner
                          </Button>
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                          }}
                        >
                          <AddPhotoAlternate
                            sx={{
                              fontSize: "48px",
                            }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            component="label"
                          >
                            <input
                              hidden
                              accept="image/*"
                              type="file"
                              // name="image"
                              id="image-upload"
                              onChange={(e) => {
                                setFieldValue("imageUrl", e.target.files[0]);
                              }}
                            />
                            Upload Banner
                          </Button>
                          <Typography
                            variant="body2"
                            sx={{ textAlign: "center" }}
                          >
                            Click to upload banner
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {console.log({ values })}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Stack gap={2}>
                      <FormikTextField label="Store Name" name="name" />
                      <Stack flexDirection={"row"} gap={2}>
                        <FormikTextField
                          label="Open Time"
                          name="openTime"
                          type="time"
                          InputLabelProps={{ shrink: true }}
                        />
                        <FormikTextField
                          label="Close Time"
                          name="closeTime"
                          type="time"
                          InputLabelProps={{ shrink: true }}
                        />
                      </Stack>

                      <Stack flexDirection={"row"} gap={2}>
                        <FormikTextField label="Latitude" name="latitude" />
                        <FormikTextField label="Longitude" name="longitude" />
                      </Stack>
                      <Stack flexDirection={"row"} gap={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={values.status === "ACTIVE"}
                              onChange={(e) =>
                                setFieldValue(
                                  "status",
                                  e.target.checked ? "ACTIVE" : "INACTIVE"
                                )
                              }
                              color="primary"
                            />
                          }
                          label={
                            <Typography
                              fontWeight={600}
                              color={
                                values.status === "ACTIVE"
                                  ? "primary"
                                  : "text.secondary"
                              }
                            >
                              {values.status === "ACTIVE"
                                ? "Active"
                                : "Inactive"}
                            </Typography>
                          }
                          sx={{ mt: 2 }}
                        />
                        <FormikTextField
                          label="Platform Fee"
                          name="platformFee"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <CurrencyRupee fontSize="16px" />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Form>
            </Box>
          </>
        )}
      </Formik>
    </Box>
  );
};

export default EditStore;
